import { Component, type ErrorInfo, type ReactNode } from 'react';

import { logger } from '@/lib/logger';

interface Props {
  /** Fallback UI rendered when a descendant throws. */
  fallback?: ReactNode;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere below it and renders a fallback instead
 * of a blank screen. Wraps the whole app in `providers.tsx`.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('Render error', error, info.componentStack);
  }

  /** Reset the boundary back to a healthy state. */
  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-warm-white p-6 text-center">
          <h1 className="text-h2 text-ink">Something went wrong</h1>
          <p className="max-w-md text-body text-warm-500">{this.state.error.message}</p>
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="mt-2 rounded-button bg-accent px-4 py-2 text-nav font-semibold text-white transition-colors hover:bg-accent-dark"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
